<template>
  <div class="h-full">
    <BaseTable
      :headers="tableHeaders"
      :list="filteredMaterialAlterCodes"
      :total-records="materialAlterCodesTotalCount"
      table-id="material-alter-codes-tab"
      :items-per-page="materialAlterCodesItemsPerPage"
      :selected-page="materialAlterCodesPageNumber"
      is-saving-layout-hidden
      clickable
      hide-grouping
      :is-add-new-hidden="mode === 'view'"
      :disable-multiselect="mode === 'view'"
      @refresh-data="onRefreshData"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNew"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters"
      @table-item-clicked="onEdit">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="p-1.5 text-red-400 rounded-full hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
      <template #filters>
        <div class="grid grid-cols-3 mx-10 my-5">
          <!-- Col 1 -->
          <div class="space-y-4 justify-self-start">
            <div>
              <BaseTriStateToggle
                :label="$t('representative.isMapped')"
                name="isInactiveFilter"
                v-model="filterIsMapped" />
            </div>
          </div>
        </div>
      </template>
    </BaseTable>

    <DCMaterialAlterCodeModalCreateEdit
      v-if="createEditModalActive && currentMaterialAlterCode"
      :material-alter-code="currentMaterialAlterCode"
      :mode="createEditModalMode"
      :view-only="props.mode === 'view'"
      :existing-material-ids="existingMaterialIds"
      :existing-material-alter-codes="materialAlterCodes"
      @close-modal="createEditModalActive = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { useI18n } from 'vue-i18n';

import BaseTable from '@/components/common/BaseTable.vue';
import DCMaterialAlterCodeModalCreateEdit from '@/components/traders/DCMaterialAlterCodeModalCreateEdit.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import BaseTriStateToggle from '../common/BaseTriStateToggle.vue';

import { RecordMode, type TableFilterData } from '@/types/Base';
import { deepCopy } from '@/utils/data';
import { getDCMaterialAlterCodesWithConditions } from '@/services/api/dataCollection';
import type { DCMaterialAlterCode, RepresentativeFiltersData } from '@/types/DataCollection';

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout?: boolean;
  resetState: boolean;
  representativeId: string;
}>();

const materialAlterCodes = defineModel<DCMaterialAlterCode[]>('materialAlterCodes', {
  default: [],
});
const materialAlterCodeModified = defineModel<boolean>('isMaterialAlterCodeModified', {
  default: false,
});
const filterIsMapped = ref<boolean | undefined>(undefined);

const originalMaterialAlterCodes = ref<DCMaterialAlterCode[]>([]);

const materialAlterCodesTotalCount = ref<number>(0);
const materialAlterCodesItemsPerPage = ref(50);
const materialAlterCodesPageNumber = ref(1);

const existingMaterialIds = computed(() => {
  return (
    materialAlterCodes.value
      ?.map((code) => code.materialAlterCode?.materialId)
      .filter((id): id is string => Boolean(id)) || []
  );
});

watch(
  () => materialAlterCodes.value,
  (newData) => {
    materialAlterCodeModified.value =
      JSON.stringify(newData) !== JSON.stringify(originalMaterialAlterCodes.value);
  },
  { deep: true }
);

async function loadMaterialAlterCodes(
  params?: TableFilterData & {
    isMapped?: boolean;
  }
) {
  const queryParams = {
    representativeId: props.representativeId,
    page: params?.page || materialAlterCodesPageNumber.value,
    itemsPerPage: params?.itemsPerPage || materialAlterCodesItemsPerPage.value,
    query: params?.query || '',
    sortColumn: params?.column !== undefined ? tableHeaders[params.column]?.value : undefined,
    sortAscending: params?.order === 'asc' || params?.order === undefined,
    isMapped: params?.isMapped,
  };

  const res = await getDCMaterialAlterCodesWithConditions(queryParams);
  if (res) {
    originalMaterialAlterCodes.value = res.data;
    materialAlterCodes.value = deepCopy<DCMaterialAlterCode[]>(originalMaterialAlterCodes.value);
    materialAlterCodesTotalCount.value = res.count;
  }
}

watch(
  () => props.resetState,
  async (newVal, oldVal) => {
    if (props.representativeId && (oldVal === undefined || (newVal && oldVal !== newVal))) {
      await loadMaterialAlterCodes();
    }
  },
  { immediate: true }
);

const { t: $t } = useI18n();

const createEditModalActive = ref(false);
const createEditModalMode = ref<'add' | 'edit' | 'view'>('add');
const currentMaterialAlterCode = ref<DCMaterialAlterCode | undefined>(undefined);

const tableHeaders = [
  {
    value: 'materialAlterCode.material.name',
    text: $t('baseObject.name'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCode.alterCode.code',
    text: $t('material.code'),
    type: 'text',
    active: true,
  },
  {
    value: 'materialAlterCodeRemotedCode',
    text: $t('representative.remotedCode'),
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const filteredMaterialAlterCodes = computed<DCMaterialAlterCode[]>(() => {
  if (!materialAlterCodes.value) return [];
  let list = materialAlterCodes.value.filter((x) => x.recordMode !== RecordMode.Deleted);

  return list;
});

function initMaterialAlterCode(): DCMaterialAlterCode {
  return {
    id: uuidv4(),
    representativeId: props.representativeId,
    isActive: true,
    isFromManager: true,
    recordMode: RecordMode.Added,
    materialAlterCode: {
      id: '',
      alterCode: {
        id: '',
        code: '',
        name: '',
        isActive: true,
        isFromManager: true,
        alterCodeTypeId: '',
        deleted: false,
      },
      measurementUnit: {
        id: '',
        code: '',
        name: '',
      },
      measurementUnitId: '',
      alterCodeId: uuidv4(),
      materialId: '',
    },
    materialAlterCodeId: uuidv4(),
    materialAlterCodeRemotedCode: '',
  };
}

function onAddNew() {
  currentMaterialAlterCode.value = initMaterialAlterCode();
  createEditModalMode.value = 'add';
  createEditModalActive.value = true;
}

function onEdit(item: DCMaterialAlterCode) {
  currentMaterialAlterCode.value = deepCopy<DCMaterialAlterCode>(item);
  createEditModalMode.value = props.mode === 'view' ? 'view' : 'edit';
  createEditModalActive.value = true;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<DCMaterialAlterCode>();

function onDeleteRow(item: DCMaterialAlterCode) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (deleteRowItem.value && materialAlterCodes.value) {
    const index = materialAlterCodes.value.findIndex((x) => x.id === deleteRowItem.value!.id);
    if (index >= 0) {
      if (materialAlterCodes.value[index].recordMode === RecordMode.Added) {
        materialAlterCodes.value.splice(index, 1);
      } else {
        materialAlterCodes.value[index].recordMode = RecordMode.Deleted;
      }
    }
  }
  modalDeleteRow.value = false;
  deleteRowItem.value = undefined;
}

function onDeleteMultiselect(items: DCMaterialAlterCode[]) {
  if (!materialAlterCodes.value) return;

  items.forEach((item) => {
    const index = materialAlterCodes.value!.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      if (materialAlterCodes.value![index].recordMode === RecordMode.Added) {
        materialAlterCodes.value!.splice(index, 1);
      } else {
        materialAlterCodes.value![index].recordMode = RecordMode.Deleted;
      }
    }
  });
}

function onSave(materialAlterCode: DCMaterialAlterCode) {
  createEditModalActive.value = false;

  if (createEditModalMode.value === 'add') {
    materialAlterCodes.value.push(materialAlterCode);
  } else {
    const index = materialAlterCodes.value.findIndex((x) => x.id === materialAlterCode.id);
    if (index >= 0) {
      if (materialAlterCodes.value[index].recordMode !== RecordMode.Added) {
        materialAlterCode.recordMode = RecordMode.Modified;
      }
      materialAlterCodes.value[index] = materialAlterCode;
    }
  }
}

const filterState = reactive<{
  query: string;
  column?: number;
  order?: 'asc' | 'desc';
}>({
  query: '',
  column: undefined,
  order: undefined,
});

async function onRefreshData(options: TableFilterData) {
  // Update filter state for local filtering (if needed)
  filterState.query = options.query ?? '';
  if (options.column !== undefined && options.order) {
    filterState.column = options.column;
    filterState.order = options.order;
  }

  // Load data with pagination parameters
  await loadMaterialAlterCodes(options);
}

async function onApplyFilters(data: RepresentativeFiltersData) {
  data.isMapped = filterIsMapped.value;
  await onRefreshData(data);
}

async function onResetFilters() {
  filterIsMapped.value = undefined;
  const params = {
    sortColumn: '',
    sortAscending: true,
  };

  await onRefreshData(params);
}
</script>
